import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';

const waiting = new Map();
let loop = false;
let webp;

/* ========================================================================= */
/* Method(s) */
/* ========================================================================= */

/**
 * Global animation frame to check for images in view
 */
const scrollFrame = () => {
    waiting.forEach((image, key) => {
        if (image?.imageRef?.current?.offsetParent) {
            const r = image.imageRef.current.getBoundingClientRect();
            if (r.top - r.height - window.innerHeight <= 0) {
                image.load();
                waiting.delete(key);
            }
        }
    });

    window.requestAnimationFrame(scrollFrame);
};

/**
 * Load all images
 */
const loadAll = () => {
    waiting.forEach((image, key) => {
        image.load();
        waiting.delete(key);
    });
};

/**
 * Confirm WEBP browser support
 */
const checkWebpSupport = async () => {
    if (typeof webp !== 'boolean') {
        if (!self.createImageBitmap) { return; }
        const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
        const blob = await fetch(webpData).then(r => r.blob());
        await createImageBitmap(blob).then(
            () => webp = true,
            () => webp = false
        );
    }

    return webp;
};

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * Hook to manage image file load on scroll
 * @param {object} options
 * @param {object} options.imageRef
 * @param {string} options.src
 * @returns {object}
 */
export const useLazyImage = ({
    imageRef,
    psrc,
    px2,
    src,
    x2
}) => {
    const [chosenSrc, setChosenSrc] = useState();
    const [chosenX2, setChosenX2] = useState();
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uid, setUid] = useState(null);

    const load = async () => setLoading(true);

    // Check WebP support
    useEffect(() => {
        if (loading && imageRef.current) {
            const webpSupport = checkWebpSupport();

            setChosenSrc(psrc && webpSupport ? psrc : src);
            setChosenX2(px2 && webpSupport ? px2 : x2);
        }
    }, [loading, imageRef.current]);

    // Load selected image
    useEffect(() => {
        if (chosenSrc && chosenX2 && loading && imageRef.current) {
            let imageNode = imageRef.current;
            const imageSrc = imageNode.currentSrc || chosenSrc;

            if (imageSrc) {
                imageNode.imageSrc = imageSrc;

                const onLoad = () => {
                    setLoading(false);
                    setLoaded(true);
                };

                imageNode.complete ? onLoad() : imageNode.onload = onLoad;
            }
        }
    }, [loading, imageRef.current, chosenSrc, chosenX2]);

    useEffect(() => {
        if (!uid) {
            const key = nanoid();
            setUid(key);

            // Register image
            waiting.set(key, {
                imageRef,
                load
            });

            // Trigger all images to load when printing document
            if (!window.onbeforeprint) {
                window.onbeforeprint = loadAll;
            }

            // Start loop
            if (!loop) {
                loop = true;
                window.requestAnimationFrame(scrollFrame);
            }
        }

        return () => { waiting.delete(uid); };
    }, []);

    return {
        loaded,
        loading,
        imageSrc: chosenSrc,
        imageX2: chosenX2
    };
};
